import * as React from "react"
import { Check, X, ChevronDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"

interface MultiSelectOption {
  label: string
  value: number
  description?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  value: number[]
  onChange: (value: number[]) => void
  placeholder?: string
  emptyMessage?: string
  maxDisplay?: number
  disabled?: boolean
  className?: string
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  maxDisplay = 3,
  disabled = false,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const selectedOptions = options.filter(opt => value.includes(opt.value))
  const displayCount = selectedOptions.length

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  const toggleOption = (optionValue: number) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue]
    onChange(newValue)
  }

  const clearAll = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    onChange([])
  }

  const removeOption = (e: React.MouseEvent | React.KeyboardEvent, optionValue: number) => {
    e.stopPropagation()
    onChange(value.filter(v => v !== optionValue))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full min-h-[44px] h-auto font-normal",
            "[&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:justify-between",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1.5 flex-1 text-left">
            {displayCount === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : (
              <>
                {selectedOptions.slice(0, maxDisplay).map(option => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="mr-1 hover:bg-gray-200"
                  >
                    {option.label}
                    <span
                      role="button"
                      tabIndex={0}
                      className="ml-1.5 inline-flex rounded-full outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => removeOption(e, option.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          removeOption(e, option.value)
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-gray-500 hover:text-gray-700" />
                    </span>
                  </Badge>
                ))}
                {displayCount > maxDisplay && (
                  <Badge variant="secondary">
                    +{displayCount - maxDisplay} more
                  </Badge>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2">
            {displayCount > 0 && (
              <span
                role="button"
                tabIndex={0}
                className="inline-flex rounded-full outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={clearAll}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    clearAll(e)
                  }
                }}
              >
                <X className="h-4 w-4 text-gray-500 hover:text-gray-700" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-10"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto overscroll-contain p-1">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500">
              {emptyMessage}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md cursor-pointer",
                    "hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
                    isSelected && "bg-blue-50"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded border",
                      isSelected
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "border-gray-300"
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-gray-500">
                        {option.description}
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
